import {
  PEN_MINUS_CURSOR,
  PEN_PLUS_CURSOR,
  PEN_TOOL_CURSOR,
} from '@/components/app/editor/tools/pen-cursor-urls'
import type { ShapeType } from '@/lib/shapes/types/shape-type'

const SHAPE_CURSOR_URLS: Record<ShapeType, string> = {
  square: '/icons/crosshair-square.svg',
  circle: '/icons/crosshair-circle.svg',
  triangle: '/icons/crosshair-triangle.svg',
  hexagon: '/icons/crosshair-hexagon.svg',
  pentagon: '/icons/crosshair-pentagon.svg',
  star: '/icons/crosshair-star.svg',
  heart: '/icons/crosshair-heart.svg',
  starone: '/icons/crosshair-starburst-one.svg',
  startwo: '/icons/crosshair-starburst-two.svg',
  path: '',
}

const TEXT_CREATION_CURSOR = 'url("/icons/crosshair-text.svg") 12 12, crosshair'

/** Crosshair cursor for the shape-creation tool; null when no shape type is picked. */
export function resolveShapeCreationCursor(selectedShape: ShapeType | null): string | null {
  if (!selectedShape) return null
  const cursorUrl = SHAPE_CURSOR_URLS[selectedShape]

  if (!cursorUrl) return 'crosshair'
  // Match design handoff hotspot for 24x24 icon boards.
  return `url("${cursorUrl}") 12 12, crosshair`
}

/** The canvas cursor for the live tool/mode; undefined leaves the inherited cursor. */
export function resolveCanvasCursor({
  isDragPanning,
  isPanInteractionActive,
  isTextLinkingActive,
  isTextDragCreating,
  isMarqueeZoomMode,
  zMarqueeKeyHeld,
  isPenEditMode,
  penEditHoverKind,
  isInPenToolMode,
  isNodeEditMode,
  isInShapeCreationMode,
  cursorUrlForShape,
  isInTextCreationMode,
}: {
  isDragPanning: boolean
  isPanInteractionActive: boolean
  isTextLinkingActive: boolean
  isTextDragCreating: boolean
  isMarqueeZoomMode: boolean
  zMarqueeKeyHeld: boolean
  isPenEditMode: boolean
  penEditHoverKind: 'endpoint' | 'anchor' | 'segment' | null
  isInPenToolMode: boolean
  isNodeEditMode: boolean
  isInShapeCreationMode: boolean
  cursorUrlForShape: string | null
  isInTextCreationMode: boolean
}): string | undefined {
  if (isDragPanning) return 'grabbing'
  if (isPanInteractionActive) return 'grab'
  // Text linking mode: pointer, except while actively drag-creating a new linked frame
  // (that keeps the text-creation crosshair per WMPCM-6852 design).
  if (isTextLinkingActive && !isTextDragCreating) return 'pointer'
  if (isMarqueeZoomMode || zMarqueeKeyHeld) return 'crosshair'
  if (isPenEditMode) {
    if (penEditHoverKind === 'anchor') return PEN_MINUS_CURSOR
    if (penEditHoverKind === 'endpoint') return PEN_TOOL_CURSOR
    if (penEditHoverKind === 'segment') return PEN_PLUS_CURSOR
    // null hover → mouse is off any path → plus cursor (consistent with <rect> default)
    return PEN_PLUS_CURSOR
  }

  if (isInPenToolMode) return PEN_TOOL_CURSOR
  if (isNodeEditMode) return undefined
  if (isInShapeCreationMode) return cursorUrlForShape ?? 'crosshair'
  if (isInTextCreationMode) return TEXT_CREATION_CURSOR
  return undefined
}
